import {
  CONSENT_TEXT_KINDS,
  CONSENT_TEXT_KIND,
  JSON_BODY_KINDS,
} from '@/constants/consent-texts'
import { consentClient, localizationHeader } from './client'

// Four states, not two, and the distinction is the whole point of this module:
//
//   published    the kind has a revision and we could read it
//   invalid      the kind has a revision, so backend validation is ON, but the body is unusable
//   unpublished  the answer arrived and did not list this kind - validation is still off
//   unavailable  the answer never arrived, so we know nothing
//
// Which version we send is decided by whether the kind is published. What we render is decided
// by whether we could read it. Collapsing `invalid` into `unpublished` would send the local
// constant while the backend is already checking versions - exactly the 422 this exists to avoid.
export const TEXT_STATUS = {
  PUBLISHED: 'published',
  INVALID: 'invalid',
  UNPUBLISHED: 'unpublished',
  UNAVAILABLE: 'unavailable',
}

// Module-level snapshot. policy.js and decision.js run outside any component and must be able to
// read the current version without reaching into the store; the Vuex module mirrors this for the
// components. One fetch per page load is complete: switching locale or country reloads the page.
let snapshot = { fetched: false, ok: false, byKind: {} }

function parseJsonBody(kind, body) {
  try {
    return JSON.parse(body)
  } catch (e) {
    console.error('consent-texts: body is not valid JSON', kind)
    return undefined
  }
}

// A channel counts only when it carries a real wording. A key present but blank is the same thing
// as a key that is absent: a checkbox with no label beside it is not something anyone can agree to.
function usableChannels(channels) {
  if (!channels || typeof channels !== 'object') return null

  const usable = {}
  Object.keys(channels).forEach((key) => {
    const text = channels[key]
    if (typeof text === 'string' && text.trim() !== '') usable[key] = text
  })

  return Object.keys(usable).length ? usable : null
}

function readCheckoutMarketing(parsed) {
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return undefined

  const channels = usableChannels(parsed.channels)
  if (!channels) return undefined

  const title = typeof parsed.title === 'string' ? parsed.title.trim() : ''

  // "I want none of it" sits at the root, not in `channels`: it is not a channel, has no field in
  // the checkout body and no row in the journal - it only unticks the three. Older revisions do
  // not carry it, and then the option is simply not shown.
  const optOut = typeof parsed.opt_out === 'string' ? parsed.opt_out.trim() : ''

  return { title: title || null, channels, optOut: optOut || null }
}

const LIST_CATEGORIES = ['technical', 'analytics', 'marketing']

function readCookieList(parsed) {
  if (!Array.isArray(parsed)) return undefined

  // A row with no name, or in a category the settings dialog has no toggle for, would simply
  // vanish from the grouped table - so it is dropped here where it can be seen instead.
  const rows = parsed.filter((row) => {
    if (!row || typeof row !== 'object') return false
    if (typeof row.name !== 'string' || row.name.trim() === '') return false
    return LIST_CATEGORIES.indexOf(row.category) !== -1
  })

  return rows.length ? rows : undefined
}

function readHtml(parsed) {
  if (typeof parsed !== 'string' || parsed.trim() === '') return undefined
  return parsed
}

function readBody(kind, body) {
  if (typeof body !== 'string' || body.trim() === '') return undefined

  const parsed = JSON_BODY_KINDS.indexOf(kind) === -1 ? body : parseJsonBody(kind, body)
  if (parsed === undefined) return undefined

  if (kind === CONSENT_TEXT_KIND.CHECKOUT_MARKETING) return readCheckoutMarketing(parsed)
  if (kind === CONSENT_TEXT_KIND.COOKIE_LIST) return readCookieList(parsed)
  return readHtml(parsed)
}

function normalizeEntry(row) {
  if (!row || CONSENT_TEXT_KINDS.indexOf(row.kind) === -1) return null

  // The version is the one part of the answer we have to hand back, so a row without one cannot
  // be treated as published: there would be nothing to send but an empty string, which is the
  // 422 case. Such a row is dropped entirely and the kind reads as unpublished.
  const version = row.version === 0 || row.version ? String(row.version).trim() : ''
  if (!version) return null

  const body = readBody(row.kind, row.body)

  return {
    kind: row.kind,
    status: body === undefined ? TEXT_STATUS.INVALID : TEXT_STATUS.PUBLISHED,
    version,
    // Locales outside cz/en/ru are served the Czech original on purpose, so this is not
    // necessarily the language that was asked for. Kept as it came; showing it is a design
    // decision, and it is never sent back - cookie-consents already carries a `locale` meaning
    // the requested one, and making the two agree would corrupt what the journal records.
    locale: row.locale || null,
    body: body === undefined ? null : body,
    // Our own client, so no camelCase interceptor - but read both spellings the way readResult()
    // in ./api.js does, in case this ever moves onto $http.
    publishedAt: row.published_at || row.publishedAt || null,
  }
}

// Never rejects. A kind that is missing, malformed or unreachable has to leave the caller on the
// texts that shipped with the build, not take the page down.
export async function loadConsentTexts(options) {
  const timeout = (options && options.timeout) || 4000

  try {
    const response = await consentClient.get('v1/consent-texts', {
      headers: localizationHeader(),
      timeout,
    })

    const rows = (response && response.data && response.data.data) || []
    const byKind = {}

    if (Array.isArray(rows)) {
      rows.forEach((row) => {
        const entry = normalizeEntry(row)
        if (entry) byKind[entry.kind] = entry
      })
    }

    snapshot = { fetched: true, ok: true, byKind }
  } catch (e) {
    console.warn('consent-texts: not loaded, falling back to the texts shipped with the build', e)
    snapshot = { fetched: true, ok: false, byKind: {} }
  }

  return snapshot
}

export function consentTextEntry(kind) {
  return snapshot.byKind[kind] || null
}

export function consentTextStatus(kind) {
  if (!snapshot.fetched || !snapshot.ok) return TEXT_STATUS.UNAVAILABLE
  const entry = consentTextEntry(kind)
  return entry ? entry.status : TEXT_STATUS.UNPUBLISHED
}

// The version to echo back. Published or invalid, the backend knows this string and is checking
// against it; otherwise the constant that shipped with the build, which is accepted precisely
// because validation for that kind is still off.
export function consentVersion(kind, fallback) {
  const entry = consentTextEntry(kind)
  return entry ? entry.version : fallback
}

// Only a revision we could actually read has a body to show.
export function consentTextBody(kind) {
  const entry = consentTextEntry(kind)
  return entry && entry.status === TEXT_STATUS.PUBLISHED ? entry.body : null
}

// True when we are sending versions we could not confirm this page load. Never travels in a
// request body - there is no field for it - it only makes a dropped decision explainable.
export function consentVersionsStale() {
  return snapshot.fetched && !snapshot.ok
}

export function consentTextsSnapshot() {
  return snapshot
}
